"use client";

import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { IconCheck, IconChevronDown, IconChevronUp } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { Z } from "@/lib/z";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SelectOption {
  value: string | number;
  label: string;
  /** Необязательная иконка слева от текста (рисуется и в списке, и в триггере). */
  icon?: React.ReactNode;
  /** Свой вид строки ВМЕСТО «значок + подпись». Нужен там, где значение —
   *  цветная метка (приоритет задачи): чип рядом с той же подписью читается как
   *  двойное название (Максим, 01.09.2026). `label` при этом остаётся —
   *  по нему список ищут с клавиатуры. */
  render?: React.ReactNode;
  disabled?: boolean;
  group?: string;
}

type Size = "xs" | "sm" | "md";

interface LegacySelectProps {
  value: string | number;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  contentClassName?: string;
  disabled?: boolean;
  size?: Size;
  /** Align popover to the right edge of trigger */
  align?: "start" | "end" | "center";
  /** Открыть список сразу при монтировании (для ленивых ячеек «клик → редактирование»). */
  defaultOpen?: boolean;
  /** Колбэк смены состояния открыт/закрыт (напр. чтобы размонтировать ленивый редактор). */
  onOpenChange?: (open: boolean) => void;
  /**
   * Свой элемент вместо стандартного поля — например бейдж статуса, по клику на
   * который открывается список. Раньше такое делали невидимым нативным <select>
   * поверх бейджа: список рисовала ОС, мимо темы портала.
   */
  trigger?: React.ReactElement;
}

const SIZE_TRIGGER: Record<Size, string> = {
  // ⚠️ Кегль пикселями, не классами Tailwind: rem в портале = 12px, из-за чего
  // text-[11px] давал 9px, а text-[12px] — 10,5px, то есть «маленький» был МЕНЬШЕ
  // «очень маленького» соседа. Теперь ступеней две: 11px и основные 12px.
  xs: "px-2 py-1 text-[11px] gap-1",
  sm: "px-2.5 py-1.5 text-[12px] gap-1.5",
  md: "px-3 py-2.5 text-[12px] gap-2",
};

const SIZE_CHEVRON: Record<Size, number> = { xs: 10, sm: 12, md: 14 };

const SIZE_ITEM: Record<Size, string> = {
  xs: "px-2.5 py-1 text-[11px]",
  sm: "px-3 py-1.5 text-[12px]",
  md: "px-3 py-2 text-[12px]",
};

// Radix Select forbids empty-string values; map "" ↔ sentinel internally.
// Whether "" means "no selection (show placeholder)" or a real option depends
// on whether the options list contains an entry with value: "". We pick the
// behavior at call site: if options has "", we treat "" as a real value via
// the EMPTY sentinel; otherwise "" → undefined to display placeholder.
const EMPTY = "__empty__";

function toRadixValue(
  v: string | number | undefined | null,
  hasEmptyOption: boolean,
): string | undefined {
  if (v === undefined || v === null) return undefined;
  const s = String(v);
  if (s === "") return hasEmptyOption ? EMPTY : undefined;
  return s;
}

function toRadixItemValue(v: string | number): string {
  const s = String(v);
  return s === "" ? EMPTY : s;
}

function fromRadixItemValue(v: string): string {
  return v === EMPTY ? "" : v;
}

// ─── Legacy-compatible API ────────────────────────────────────────────────────

export function Select({
  value,
  onChange,
  options,
  placeholder = "Выбрать…",
  className,
  contentClassName,
  disabled,
  size = "md",
  align = "start",
  defaultOpen,
  onOpenChange,
  trigger,
}: LegacySelectProps) {
  const hasEmptyOption = options.some((o) => String(o.value) === "");
  // Group options if any have `group`
  const hasGroups = options.some((o) => o.group);
  const groups: Array<{ label?: string; items: SelectOption[] }> = [];
  if (hasGroups) {
    const ungrouped = options.filter((o) => !o.group);
    if (ungrouped.length) groups.push({ items: ungrouped });
    const map = new Map<string, SelectOption[]>();
    for (const o of options) {
      if (!o.group) continue;
      if (!map.has(o.group)) map.set(o.group, []);
      map.get(o.group)!.push(o);
    }
    for (const [label, items] of map) groups.push({ label, items });
  } else {
    groups.push({ items: options });
  }

  return (
    <SelectPrimitive.Root
      value={toRadixValue(value, hasEmptyOption)}
      onValueChange={(v) => onChange(fromRadixItemValue(v))}
      disabled={disabled}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
    >
      {trigger ? (
        <SelectPrimitive.Trigger asChild>{trigger}</SelectPrimitive.Trigger>
      ) : (
      <SelectPrimitive.Trigger
        className={cn(
          // Форма — ИЗ ТЕМЫ в обоих размерах. Компактный список был пилюлей
          // (rounded-full): при общем радиусе 18 это смотрелось уместно, но
          // после перехода на 8 круглый триггер рядом с прямоугольными полями и
          // кнопками выпадает из строя (Максим, 26.08.2026).
          "flex w-full items-center justify-between overflow-hidden border border-[var(--border)] bg-[var(--surface)] text-left transition-colors outline-none",
          size === "md" ? "rounded-[var(--radius-lg)]" : "rounded-[var(--radius-md)]",
          "[&>span:first-child]:block [&>span:first-child]:min-w-0 [&>span:first-child]:flex-1 [&>span:first-child]:truncate",
          "data-[state=open]:border-[var(--primary)]/50 data-[state=open]:ring-2 data-[state=open]:ring-[var(--primary)]/10",
          "hover:border-[var(--primary)]/30",
          "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
          "data-[placeholder]:[&>span:first-child]:text-[var(--textMuted)]",
          SIZE_TRIGGER[size],
          className,
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} className="min-w-0 flex-1 truncate text-left" />
        <SelectPrimitive.Icon asChild>
          <IconChevronDown
            size={SIZE_CHEVRON[size]}
            className="shrink-0 text-[var(--textMuted)] transition-transform duration-150 data-[state=open]:rotate-180"
          />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      )}

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          align={align}
          // Слой — из единой шкалы (lib/z.ts). Раньше стояло z-[200] с расчётом
          // на HrModal (z-[100]), но под модалками z-[1000]+ список уезжал вниз.
          style={{ zIndex: Z.DROPDOWN }}
          className={cn(
            "overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] shadow-[0_8px_32px_rgba(0,0,0,0.12)]",
            "min-w-[var(--radix-select-trigger-width)] max-h-[var(--radix-select-content-available-height)]",
            contentClassName,
          )}
        >
          <SelectPrimitive.ScrollUpButton className="flex h-6 items-center justify-center text-[var(--textMuted)]">
            <IconChevronUp size={12} />
          </SelectPrimitive.ScrollUpButton>
          <SelectPrimitive.Viewport className="py-1 scrollbar-autohide">
            {groups.map((g, gi) => (
              <React.Fragment key={gi}>
                {g.label && (
                  <SelectPrimitive.Group>
                    <SelectPrimitive.Label className="field-label px-3 pb-0.5 pt-2">
                      {g.label}
                    </SelectPrimitive.Label>
                    {g.items.map((o) => (
                      <Item key={o.value} option={o} size={size} />
                    ))}
                  </SelectPrimitive.Group>
                )}
                {!g.label &&
                  g.items.map((o) => <Item key={o.value} option={o} size={size} />)}
              </React.Fragment>
            ))}
          </SelectPrimitive.Viewport>
          <SelectPrimitive.ScrollDownButton className="flex h-6 items-center justify-center text-[var(--textMuted)]">
            <IconChevronDown size={12} />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

function Item({ option, size }: { option: SelectOption; size: Size }) {
  return (
    <SelectPrimitive.Item
      value={toRadixItemValue(option.value)}
      disabled={option.disabled}
      className={cn(
        // .menu-item — ОДИН стандарт строки списка на весь портал (globals.css).
        // Здесь было своё описание: свои отступы, свой радиус, своя подсветка
        // через примесь фирменного. Рукописные выпадашки описывали то же самое
        // иначе, и одинаковые по смыслу списки выглядели по-разному.
        // Теперь вид живёт в одном месте; здесь остаются только отступы под
        // размер и поведение Radix.
        "menu-item relative select-none mx-1",
        SIZE_ITEM[size],
      )}
    >
      <SelectPrimitive.ItemText asChild>
        <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate">
          {option.render ?? (
            <>
              {option.icon && <span className="shrink-0 inline-flex items-center">{option.icon}</span>}
              <span className="truncate">{option.label}</span>
            </>
          )}
        </span>
      </SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator>
        <IconCheck size={12} className="shrink-0 text-[var(--primary)]" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

// ─── Compound API (re-export) ─────────────────────────────────────────────────

export const SelectRoot = SelectPrimitive.Root;
export const SelectTrigger = SelectPrimitive.Trigger;
export const SelectValue = SelectPrimitive.Value;
export const SelectContent = SelectPrimitive.Content;
export const SelectItem = SelectPrimitive.Item;
export const SelectItemText = SelectPrimitive.ItemText;
export const SelectGroup = SelectPrimitive.Group;
export const SelectLabel = SelectPrimitive.Label;
export const SelectSeparator = SelectPrimitive.Separator;
export const SelectPortal = SelectPrimitive.Portal;
